import Repeater, { type RepeaterInputRef } from '@/Components/inputs/Repeater';
import TextField from '@/Components/inputs/Text';
import type { ContentLinksState } from '@/Components/inputs/repeaters/ContentLinks';
import { SocialLinksRepeater, type SocialLinksState, useSocialLinksRepeaterRef } from '@/Components/inputs/repeaters/SocialLinks';
import { MimeTypeEnum } from '@/graphql/types';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import { Grid2, MenuItem, Select, type SelectProps, Stack, type TextFieldProps } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ImagePickerWithPreview from '@/Components/inputs/ImagePickerWithPreview/ImagePickerWithPreview';
import SettingsActionBar from '@/Components/settings/SettingsActionBar';
import { useUser } from '@/Utility/hooks';
import { useUpdateUserMutation } from '@/graphql/mutations/User.mutation';
import type { User, User_Meta_ProfileArticleLinks_RepeaterInput, User_Meta_ProfileSocialLinks_RepeaterInput } from '@/graphql/types';
import { createFileRoute } from '@tanstack/react-router';
import type { AssetNode } from '../../-utils';
const ContentLinks = React.lazy(() => import(/* webpackChunkName: "components/repeater/social-links" */'@/Components/inputs/repeaters/ContentLinks'));
const DESCRIPTION_MAX_CHAR_LIMIT = 150;
const SLUG_MAX_CHAR_LIMIT = 60;
export const Profile = () => {
  const {
    user
  } = useUser();
  const profileSocialLinksRepeaterRef = useSocialLinksRepeaterRef();
  const profileArticlesLinksRepeaterRef = useRef<RepeaterInputRef<ContentLinksState>>(null);
  const [userState, setUserState] = useState({
    ...user
  });
  const [updateUser, isUpdatingUser] = useUpdateUserMutation();
  const isChanged = useMemo(() => {
    const socialData: User_Meta_ProfileSocialLinks_RepeaterInput[] = profileSocialLinksRepeaterRef?.current?.getData()!;
    const articleData: User_Meta_ProfileArticleLinks_RepeaterInput[] = profileArticlesLinksRepeaterRef?.current?.getData()!;
    const hasRepeaterContentChanged = !!profileArticlesLinksRepeaterRef.current && JSON.stringify(user?.meta?.profileArticleLinks) !== JSON.stringify(articleData) || !!profileSocialLinksRepeaterRef.current && JSON.stringify(user?.meta?.profileSocialLinks) !== JSON.stringify(socialData);
    return JSON.stringify(user) !== JSON.stringify(userState) || hasRepeaterContentChanged;
  }, [user, userState]);
  const muiProps: Omit<TextFieldProps, 'ref' | 'defaultValue'> = {
    size: 'small',
    variant: 'outlined',
    fullWidth: true
  };
  const textProps = (key: keyof typeof userState): Omit<TextFieldProps, 'ref' | 'defaultValue'> => ({
    ...(muiProps as unknown as TextFieldProps),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      let newVal = e.target.value;
      if (key === 'nicename') {
        // validate slug - cannot contain spaces and special characters
        newVal = newVal.replaceAll(' ', '-').replaceAll(/[^\w-]+/g, '').slice(0, SLUG_MAX_CHAR_LIMIT).toLowerCase();
      }
      setUserState(prev => ({
        ...prev,
        [key]: newVal
      }));
    },
    value: userState[key]
  });
  const profileImageProps = {
    onClear: () => {
      setUserState(prev => ({
        ...prev,
        meta: {
          ...prev.meta,
          profileImage: null
        }
      }));
    },
    onFileSelect: (file: AssetNode) => {
      if (file === null || !file) return;
      if (file?.id === userState?.meta?.profileImage?.id) return;
      setUserState(prev => ({
        ...prev,
        meta: {
          ...prev?.meta,
          profileImage: file
        }
      }) as unknown as User);
    },
    mimeTypes: [MimeTypeEnum.ImageJpeg, MimeTypeEnum.ImageGif, MimeTypeEnum.ImagePng],
    buttonTitle: 'Replace',
    buttonIcon: <FindReplaceIcon />,
    buttonStyle: {
      width: '100%'
    } as React.CSSProperties
  };
  const onSave = () => {
    if (isUpdatingUser) return;
    const socialData: User_Meta_ProfileSocialLinks_RepeaterInput[] = profileSocialLinksRepeaterRef?.current?.getFormattedData()!;
    const articleData: User_Meta_ProfileArticleLinks_RepeaterInput[] = profileArticlesLinksRepeaterRef?.current?.getFormattedData()!;
    if (!user?.id) {
      return;
    }
    updateUser({
      input: {
        id: user?.id,
        firstName: userState.firstName,
        lastName: userState.lastName,
        description: userState.description,
        jobRole: userState?.meta?.jobRole,
        profileImage: userState?.meta?.profileImage?.id,
        displayName: `${userState?.firstName} ${userState?.lastName}`,
        profileArticleLinks: articleData,
        profileSocialLinks: socialData,
        nicename: userState?.nicename
      }
    }, {
      onCompleted: (data, errors) => {
        if (errors) {
          toast.error(`Something went wrong: ${errors.map(e => e.message).join(', ')}`);
          return;
        }
        setUserState({
          ...(data?.updateUser?.user as unknown as User)
        });
        profileArticlesLinksRepeaterRef?.current?.reset(data?.updateUser?.user?.meta?.profileArticleLinks as unknown as ContentLinksState[] || []);
        profileSocialLinksRepeaterRef?.current?.reset(data?.updateUser?.user?.meta?.profileSocialLinks as unknown as SocialLinksState[] || []);
        toast.success('User updated successfully');
      }
    });
  };
  return <>
      <SettingsActionBar canSave={isChanged} loading={isUpdatingUser} onReset={() => {
      setUserState({
        ...user
      });
      profileSocialLinksRepeaterRef.current?.reset(user?.meta?.profileSocialLinks as SocialLinksState[]);
      profileArticlesLinksRepeaterRef.current?.reset(user?.meta?.profileArticleLinks as ContentLinksState[]);
    }} onSave={onSave} />
      <Grid2 className="SettingsTabGrid" container spacing={0} style={{
      marginBottom: '30px'
    }}>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Username</h3>
          <TextField label="Username" disabled {...textProps('username')} />
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Role</h3>
          <Select label="Role" disabled defaultValue={userState.role} {...muiProps as SelectProps}>
            <MenuItem value="super_admin">Super Admin</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="user_sales">Sales User</MenuItem>
            <MenuItem value="user_sales_restricted">Sales User (Restricted)</MenuItem>
            <MenuItem value="user">User</MenuItem>
          </Select>
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Nickname</h3>
          <TextField disabled label="Nickname" {...textProps('nickname')} />
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Email</h3>
          <TextField disabled label="Email" {...textProps('email')} />
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>First Name</h3>
          <TextField label="First name" {...textProps('firstName')} />
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Last Name</h3>
          <TextField label="Last name" {...textProps('lastName')} />
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Slug</h3>
          <p>
            Url Friendly Name: <a>{`${window.location.origin}/users/${userState?.nicename}`}</a>
          </p>
          <TextField label="Slug" {...textProps('nicename')} slotProps={{
          htmlInput: {
            maxLength: SLUG_MAX_CHAR_LIMIT
          }
        }} />
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Job Role</h3>
          <TextField label="Job Role" {...muiProps} onChange={e => {
          setUserState(prev => ({
            ...prev,
            meta: {
              ...prev.meta,
              jobRole: e.target.value
            }
          }));
        }} value={userState?.meta?.jobRole} />
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Brief Description</h3>
          <TextField label="Brief Description" slotProps={{
          htmlInput: {
            maxLength: DESCRIPTION_MAX_CHAR_LIMIT
          }
        }} {...textProps('description')} />
          <p style={{
          marginBottom: 0
        }}>
            Characters: {userState?.description?.length ?? 0}/{DESCRIPTION_MAX_CHAR_LIMIT}
          </p>
        </Grid2>
        <Grid2 size={{
        sm: 12,
        md: 6
      }}>
          <h3>Profile Image</h3>
          <ImagePickerWithPreview fileEventProps={profileImageProps as React.ComponentProps<typeof ImagePickerWithPreview>['fileEventProps']} image={userState?.meta?.profileImage as AssetNode} />
        </Grid2>
        <Grid2 size={{
        sm: 12
      }}>
          <Stack direction="row" justifyContent="space-between" alignContent="flex-start" sx={{
          mb: 1
        }}>
            <div>
              <h3>Social Icons</h3>
              <p>Social Icon Buttons that link your matching social media account</p>
            </div>
          </Stack>

          <SocialLinksRepeater repeaterStyle={{
          maxHeight: '400px',
          overflowX: 'hidden',
          overflowY: 'auto'
        }} ref={profileSocialLinksRepeaterRef} initState={user?.meta?.profileSocialLinks as SocialLinksState[]} onStateChange={data => {
          setUserState(prev => ({
            ...prev,
            meta: {
              ...prev.meta,
              profileSocialLinks: data
            }
          }));
        }} limit={2} onDragChange={(setState, value) => {
          if (typeof value !== 'string') return;
          const initialState: SocialLinksState = {
            url: value,
            name: ''
          };
          setState(prev => [...prev, initialState]);
        }} dragMessage="Drop here to add in URL column" />
        </Grid2>
        <Grid2 size={{
        sm: 12
      }}>
          <Stack direction="row" justifyContent="space-between" alignContent="flex-start" sx={{
          mb: 1
        }}>
            <div>
              <h3>Articles/Blogs/News Links</h3>
              <p>Links to news/blog-like content you want to share with clients</p>
            </div>
          </Stack>

          <Repeater repeaterStyle={{
          maxHeight: '400px',
          overflowX: 'hidden',
          overflowY: 'auto'
        }}
        // biome-ignore lint/suspicious/noExplicitAny: need to figure out a better way to do this
        rowEl={ContentLinks as any} ref={profileArticlesLinksRepeaterRef} initState={user?.meta?.profileArticleLinks as ContentLinksState[]} onStateChange={data => {
          setUserState(prev => ({
            ...prev,
            meta: {
              ...prev.meta,
              profileArticleLinks: data
            }
          }));
        }} limit={10} onDragChange={(setState, value: string | File) => {
          if (typeof value !== 'string') return;
          const initialState: ContentLinksState = {
            url: value,
            name: '',
            iconName: ''
          };
          setState(prev => [...prev, initialState]);
        }} dragMessage="Drop here to add in URL column" />
        </Grid2>
        {/* password section */}
        {/* <Grid size={{ sm: 12}}>
          <Button className="z-btn-3">
            <span>Set New Password</span>
          </Button>
         </Grid> */}
      </Grid2>
    </>;
};
export const Route = createFileRoute('/dash/settings/management/profile/')({
  component: Profile
});